import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaChalkboardTeacher,
  FaBullhorn,
  FaSchool,
  FaLeaf,
  FaRegHeart,
} from "react-icons/fa";
import "./QualityEducation.css";

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const QualityEducation = () => {
  const initiatives = [
    {
      icon: FaUsers,
      text: "Integrating marginalized communities into the mainstream education system.",
    },
    {
      icon: FaChalkboardTeacher,
      text: "Organizing workshops on the importance of education at block, district, and state levels.",
    },
    {
      icon: FaBullhorn,
      text: "Creating a interface between the community and education authorities through campaigns.",
    },
    {
      icon: FaSchool,
      text: "Successfully integrating children with mental health challenges into mainstream schools.",
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* ================= Hero (1521 x 482) ================= */}
      <section className="qe-page">
        <div className="qe-container">
          <div className="qe-hero">
            <div
              className="qe-hero__image"
              style={{ backgroundImage: "url(/assets/images/backgrounds/Quality.png)" }}
            />
            <div className="qe-hero__overlay" />
            <div className="qe-hero__content">
              <motion.h2
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="qe-hero__title"
              >
                Quality Education
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="qe-hero__text"
              >
                Vikash advocates for quality education by offering resources,
                training, and support to ensure all children have access to
                effective learning opportunities.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= Introduction ================= */}
      <section className="qe-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center justify-content-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <h2 className="qe-heading">Building a Foundation for the Future</h2>
                <span className="qe-heading__underline" />
                <p className="qe-intro__text">
                  Our core mission is to bridge educational gaps and ensure
                  every child has the chance to learn and grow. We focus on
                  creating an inclusive environment, empowering communities,
                  and fostering collaboration between parents and educational
                  authorities to build a stronger, more equitable education
                  system for all.
                </p>
              </motion.div>
              <motion.div className="col-lg-6" variants={staggerItem}>
                <Row className="g-3">
                  <Col xs={4}>
                    <img
                      src="/assets/images/sections/Qe1.jpg"
                      alt="Children in classroom"
                      className="qe-intro__image"
                    />
                  </Col>
                  <Col xs={4}>
                    <img
                      src="/assets/images/sections/Qe2.jpg"
                      alt="Student learning"
                      className="qe-intro__image"
                    />
                  </Col>
                  <Col xs={4}>
                    <img
                      src="/assets/images/sections/Qe3.jpg"
                      alt="Community education meeting"
                      className="qe-intro__image"
                    />
                  </Col>
                </Row>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= Key Initiatives ================= */}
      <section className="qe-approach">
        <span className="qe-approach__leaf" aria-hidden="true">
          <FaLeaf />
        </span>
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="qe-approach__header"
          >
            <span className="qe-eyebrow">Our Key Initiatives</span>
            <h2 className="qe-heading qe-heading--center">
              We employ a multi-faceted approach to tackle educational
              challenges from the ground up.
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4">
              {initiatives.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Col md={6} lg={3} key={index}>
                    <motion.div variants={staggerItem} className="qe-card">
                      <span className="qe-card__icon">
                        <Icon />
                      </span>
                      <p className="qe-card__text">{item.text}</p>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="qe-cta">
        <Container>
          <motion.div
            className="qe-cta__card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <h2 className="qe-cta__title">Support a Child's Future</h2>
            <p className="qe-cta__text">
              Your contribution helps provide essential learning materials
              and support to children in need.
            </p>
            <div className="qe-cta__actions">
              <button
                type="button"
                className="qe-btn qe-btn--solid"
                onClick={openDonation}
              >
                <FaRegHeart /> Donate
              </button>
              <button
                type="button"
                className="qe-btn qe-btn--outline"
                onClick={() => navigate("/interns")}
              >
                <FaUsers /> Join Us
              </button>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default QualityEducation;